import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AnimatedModal from "components/layout/motion/AnimatedModal";
import FunctionalButton from "components/layout/FunctionalButton";
import { validateAndNormalizeMapBaseName } from "lib/protocols/mapLoad";

interface SaveCurrentMapNameDialogProps {
    visible: boolean;
    busy?: boolean;
    onConfirm: (normalizedBaseName: string) => void;
    onCancel: () => void;
}

/**
 * Name prompt used by create-mission "Current map" flow before saving the on-robot map.
 */
const SaveCurrentMapNameDialog: React.FC<SaveCurrentMapNameDialogProps> = ({
    visible,
    busy = false,
    onConfirm,
    onCancel,
}) => {
    const { t } = useTranslation(["missions", "common"]);
    const [name, setName] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        if (!visible) return;
        setName("");
        setError("");
    }, [visible]);

    const handleConfirm = () => {
        setError("");
        try {
            const normalized = validateAndNormalizeMapBaseName(name);
            onConfirm(normalized);
        } catch (e) {
            setError(e instanceof Error ? e.message : t("wizard.currentMapNameInvalid"));
        }
    };

    return (
        <AnimatedModal
            visible={visible}
            onClose={busy ? () => {} : onCancel}
            closeOnBackdrop={!busy}
            className="max-w-md rounded-r10 bg-black100 p-6"
            zIndexClassName="z-[220]"
        >
            <h2 className="mb-2 text-h4 font-semibold">{t("wizard.currentMapNameTitle")}</h2>
            <p className="mb-4 text-h5 text-black40">{t("wizard.currentMapNameHint")}</p>
            <label className="mb-1 block text-h5 text-black40" htmlFor="current-map-name">
                {t("wizard.currentMapNameLabel")}
            </label>
            <input
                id="current-map-name"
                type="text"
                className="mb-2 w-full rounded-r5 border border-black70 bg-black90 px-3 py-2 text-h5 outline-none focus:border-primary2"
                placeholder={t("wizard.currentMapNamePlaceholder")}
                value={name}
                disabled={busy}
                autoFocus
                onChange={(e) => {
                    setName(e.target.value);
                    if (error) setError("");
                }}
                onKeyDown={(e) => {
                    if (e.key === "Enter" && !busy) handleConfirm();
                }}
            />
            <div className="mb-4 min-h-6">
                {error ? <p className="text-h5 text-warning">{error}</p> : null}
            </div>
            <div className="flex justify-end gap-3">
                <FunctionalButton
                    title={t("common:cancel")}
                    className="bg-black70 px-4 py-2"
                    disabled={busy}
                    onClick={onCancel}
                />
                <FunctionalButton
                    title={busy ? t("wizard.currentMapSaving") : t("common:save")}
                    className="px-4 py-2 disabled:opacity-50"
                    disabled={busy}
                    onClick={handleConfirm}
                />
            </div>
        </AnimatedModal>
    );
};

export default SaveCurrentMapNameDialog;
